import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
  viewChildren,
  effect,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { HasFeatureModule } from '@fc-shared/directives/has-feature/has-feature.module';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { OverlayClickDirective } from '@fc-shared/ui/fc-overlay/overlay-click.directive';
import { OverlayContentDirective } from '@fc-shared/ui/fc-overlay/overlay-content.directive';
import { QuickSearchResultsComponent } from './quick-search-results.component';
import { QuickSearchFacadeService } from '../services/quick-search-facade.service';
import { NgButtonComponent } from '@fc-shared/ui/buttons/ng-button';
import { QuickSearchInputComponent } from './quick-search-input.component';

@Component({
  selector: 'fc-quick-search',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-container *fcHasFeature="'search'">
      <div
        class="hidden sm:flex items-center gap-8 border border-secondary-200 rounded-lg h-36 px-12 cursor-pointer hover:bg-primary-50 w-200 md:w-300 "
        fcOverlayClick
        #searchTrigger
      >
        <!-- z-0 fix for icon overlapping mobile headers -->
        <fc-icon [size]="20" icon="icon-search-outline" class="z-0!" />
        <span class="text-secondary-300 text-sm">
          Press {{ searchShortcut() }} to search
        </span>
      </div>
      <div class="sm:hidden">
        <ng-button
          variant="icon"
          icon="icon-search-outline"
          aria-label="Open Search"
          (click)="openMobileSearch()"
        />
      </div>
      @if (mobileOpen()) {
        <div class="sm:hidden fixed inset-0 z-[200] bg-white flex flex-col">
          <div
            class="flex items-center gap-8 px-12 h-64 border-b border-secondary-100"
          >
            <button
              type="button"
              (click)="closeMobileSearch()"
              class="flex items-center justify-center size-36 rounded-lg hover:bg-secondary-50 border-0 bg-transparent cursor-pointer"
            >
              <fc-icon
                icon="icon-arrow-back"
                [size]="20"
                color="secondary600"
              />
            </button>
            <fc-quick-search-input [autoFocus]="true" class="flex-1 m-0" />
          </div>
          <fc-quick-search-results class="flex-1 min-h-0 flex flex-col" />
        </div>
      }

      <div
        [fcOverlay]="searchTrigger"
        [position]="'top-center'"
        #overlayContent
      >
        <div class="flex flex-col gap-8 w-[660px] ">
          <fc-quick-search-input #searchInput />
          <fc-quick-search-results [active]="overlayActive()" />
        </div>
      </div>
    </ng-container>
  `,
  imports: [
    HasFeatureModule,
    IconComponent,
    OverlayClickDirective,
    OverlayContentDirective,
    QuickSearchResultsComponent,
    NgButtonComponent,
    QuickSearchInputComponent,
  ],
})
export class QuickSearchComponent {
  facade = inject(QuickSearchFacadeService);
  mobileOpen = signal(false);
  searchShortcut = computed(() =>
    /Macintosh|iPhone|iPad|iPod/.test(navigator.userAgent)
      ? '⌘ + K'
      : 'Ctrl + K',
  );
  overlayActive = computed(
    () => this.overlayTrigger()?.isOpenSignal() ?? false,
  );
  private searchTriggerEl = viewChild('searchTrigger', { read: ElementRef });
  private overlayTrigger = viewChild('searchTrigger', {
    read: OverlayClickDirective,
  });
  private searchInput = viewChild<QuickSearchInputComponent>('searchInput');
  private searchInputs = viewChildren(QuickSearchInputComponent);
  private overlayContent = viewChild<ElementRef>('overlayContent');
  private destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      if (this.facade.openRequested() > 0) {
        this.openSearch();
      }
    });

    effect(() => {
      if (this.facade.focusInputRequested() > 0) {
        this.focusActiveSearchInput();
      }
    });

    effect(() => {
      if (this.facade.mobileCloseRequested() > 0) {
        this.mobileOpen.set(false);
        this.facade.isSearchOpen.set(false);
      }
    });

    effect(() => {
      const el = this.overlayContent()?.nativeElement;
      if (!el) return;
      fromEvent<ToggleEvent>(el, 'toggle')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (e.newState === 'closed') {
            this.facade.isSearchOpen.set(false);
            this.facade.clearSearch();
          } else if (e.newState === 'open') {
            this.facade.isSearchOpen.set(true);
            this.facade.resetSelection();
            this.searchInput()?.focus();
          }
        });
    });
  }

  private focusActiveSearchInput(): void {
    this.searchInputs()[0]?.focus();
  }

  openSearch(): void {
    this.searchTriggerEl()?.nativeElement.click();
    this.facade.resetSelection();
  }

  openMobileSearch(): void {
    this.mobileOpen.set(true);
    this.facade.isSearchOpen.set(true);
    this.facade.resetSelection();
  }

  closeMobileSearch(): void {
    this.mobileOpen.set(false);
    this.facade.isSearchOpen.set(false);
    this.facade.clearSearch();
  }
}
